import type { JSX } from 'preact';
import type { RacecraftRace, RacecraftReport } from '../../sim/bot/index.ts';
import { CLOSE_GAP_M } from '../../sim/bot/index.ts';

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

function one(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function signed(value: number, digits = 1): string {
  const text = Math.abs(value).toFixed(digits);
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}

function ordinal(place: number): string {
  const rounded = Math.round(place);

  if (rounded !== place) {
    return place.toFixed(1);
  }

  return rounded === 1 ? '1st' : rounded === 2 ? '2nd' : rounded === 3 ? '3rd' : `${rounded}th`;
}

const ORDER_LABEL: Readonly<Record<RacecraftRace['order'], string>> = {
  first: 'Moving first',
  last: 'Moving last',
};

const paid = (race: RacecraftRace) => race.caused.medium + race.caused.hard;

/**
 * A pack test in the playtest history, under the shared lead (pace and boat-clock): its
 * racecraft per race, then where it placed in each seat order next to Expert in that seat.
 */
export function PackHistoryDetail({ report }: { report: RacecraftReport }): JSX.Element {
  const s = report.summary;

  return (
    <>
      <span class="bot-history-detail" data-bot-history-racecraft>
        Per race: {one(s.squeezes)} squeezes · {one(s.blocks)} blocks · {one(s.paidHits)} paid hits caused,{' '}
        {one(s.nudges)} nudges · close on {percent(s.closeShare)} of moves · places {signed(s.netPlaces)}
      </span>
      <span class="bot-history-detail">
        {report.races
          .map((race) => `${ORDER_LABEL[race.order]}: ${ordinal(race.place)} (Expert ${ordinal(race.expertPlace)})`)
          .join(' · ')}
        {' · '}Expert slowed {signed(s.rivalDelayMs / 1000)} s
      </span>
    </>
  );
}

/**
 * Under the chart when a pack test is selected: one row per race, and what each bar should
 * move, so a reader can check Aggression and Manners against their hints.
 */
export function PackLog({ report }: { report: RacecraftReport }): JSX.Element {
  const crashes = report.races.reduce((sum, race) => sum + race.crashes, 0);

  return (
    <details class="bot-playtest-log" data-bot-pack-log open>
      <summary>
        Pack log · vs {report.opponents} Expert{report.opponents === 1 ? '' : 's'}, both seat orders · {crashes}{' '}
        {crashes === 1 ? 'crash' : 'crashes'}
      </summary>
      <div class="bot-pack-log-body">
        <table class="bot-pack-table">
          <thead>
            <tr>
              <th scope="col">Seat order</th>
              <th scope="col" title="Its place; Expert's place in the same seat in an all-Expert pack">
                Place (Expert)
              </th>
              <th scope="col" title="Its time over Expert's in the same seat">Time</th>
              <th scope="col" title="Moves where it chose a click that brushes a rival">Squeezes</th>
              <th scope="col" title="Moves where it took a slower click to hold a rival up">Blocks</th>
              <th scope="col" title="Medium or hard hits it caused / nudges / paid hits it suffered">
                Hits
              </th>
              <th scope="col" title={`Its moves that ended within ${CLOSE_GAP_M} m of a rival's hull`}>
                Close
              </th>
              <th scope="col" title="Passes made / times passed">Passes</th>
              <th scope="col" title="The Expert's time here, less its time racing another Expert">
                Expert slowed
              </th>
            </tr>
          </thead>
          <tbody>
            {report.races.map((race) => (
              <tr key={`${race.order}-${race.seed}`} data-pack-order={race.order}>
                <td>
                  {ORDER_LABEL[race.order]}
                  {race.finished ? '' : ' (did not finish)'}
                </td>
                <td>
                  {ordinal(race.place)} ({ordinal(race.expertPlace)})
                </td>
                <td>{percent(race.timeMs / Math.max(1, race.expertMs))}</td>
                <td>{race.squeezes}</td>
                <td>{race.blocks}</td>
                <td>
                  {paid(race)} / {race.caused.nudge} / {race.suffered.medium + race.suffered.hard}
                </td>
                <td>
                  {race.closeMoves} of {race.moves}
                  {race.minGapM === null ? '' : `, ${race.minGapM.toFixed(1)} m least`}
                </td>
                <td>
                  {race.passes} / {race.passedBy}
                </td>
                <td>{signed(race.rivalDelayMs / 1000)} s</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p class="ui-hint">
          Aggression from 15 should add squeezes, and above 50 blocks. Manners from 50 should leave more
          room (fewer close moves) and hold blocking back, but only with Aggression 15 or more: below
          that the bot does not read rivals at all. Head to head these move a fraction of a squeeze or
          block per race; one map says little. A paid hit is one it was at fault for.
        </p>
      </div>
    </details>
  );
}
